/**
 * Web billing through Stripe (plan Phase 7, decision D4). Used only by the
 * web copies of the purchase screens; the phone buys through the app
 * stores and never imports this.
 *
 * Checkout and the customer portal are pages Stripe hosts: the server
 * returns a URL and the browser goes there. On return, Stripe sends the
 * person to /Profile/SubscriptionManagement with a `checkout` query
 * (after checkout) or `portal=return` (after the customer portal).
 */
import http from './http';
import { createLogger } from '../utils/logger';

const logger = createLogger('billing');

/** The tier ladder, sold monthly and yearly. */
export type LadderTier = 'letter' | 'basic' | 'plus' | 'premium';
/** Family plans, yearly only; the owner gets the base tier and a family group. */
export type FamilyPlan = 'basic_spouse' | 'plus_spouse' | 'premium_spouse' | 'family_bundle';
/** Everything the web can sell. `founding` is Premium at the locked-in yearly price. */
export type PaidTier = LadderTier | 'founding' | FamilyPlan;
export type BillingPeriod = 'monthly' | 'yearly';

export interface CatalogPrice {
  priceId: string;
  /** Minor units, e.g. cents. */
  amount: number;
  currency: string;
}

export interface CatalogTier {
  tier: PaidTier;
  name: string;
  headline: string;
  monthly: CatalogPrice | null;
  yearly: CatalogPrice | null;
}

export interface FoundingTier extends CatalogTier {
  available: boolean;
  slotsRemaining: number;
  totalSlots: number;
}

export interface Catalog {
  enabled: boolean;
  /** letter, basic, plus, premium — whichever have prices configured. */
  tiers: CatalogTier[];
  /** The founding-member offer, when a price is configured. */
  founding: FoundingTier | null;
  /** Family plans with a price configured. */
  family: CatalogTier[];
}

const EMPTY_CATALOG: Catalog = { enabled: false, tiers: [], founding: null, family: [] };

function browserOrigin(): string | undefined {
  const g = globalThis as unknown as { location?: { origin?: string } };
  return g.location?.origin;
}

function go(url: string): void {
  const g = globalThis as unknown as { location?: { assign?: (u: string) => void } };
  g.location?.assign?.(url);
}

export function formatAmount(price: CatalogPrice | null): string {
  if (!price) return '';
  const major = price.amount / 100;
  const symbol = price.currency.toLowerCase() === 'usd' ? '$' : `${price.currency.toUpperCase()} `;
  return `${symbol}${major.toFixed(2)}`;
}

class BillingService {
  async getCatalog(): Promise<Catalog> {
    try {
      const response = await http.get('/api/billing/catalog');
      const data = (response.data ?? {}) as Partial<Catalog>;
      return {
        enabled: !!data.enabled,
        tiers: data.tiers ?? [],
        founding: data.founding ?? null,
        family: data.family ?? [],
      };
    } catch (error) {
      logger.debug('Catalog unavailable:', error);
      return EMPTY_CATALOG;
    }
  }

  /** Asks the server for a Checkout page and sends the browser there. */
  async startCheckout(tier: PaidTier, period: BillingPeriod): Promise<void> {
    const response = await http.post('/api/billing/checkout-session', {
      tier,
      period,
      origin: browserOrigin(),
    });
    const url = response.data?.data?.url;
    if (!url) throw new Error('No checkout page was returned.');
    go(url);
  }

  /** Opens Stripe's customer portal: cancel, change plan, update card. */
  async openPortal(): Promise<void> {
    const response = await http.post('/api/billing/portal-session', { origin: browserOrigin() });
    const url = response.data?.data?.url;
    if (!url) throw new Error('No billing page was returned.');
    go(url);
  }
}

export const billingService = new BillingService();
